<template>
  <template v-if="isLoggedIn">
    <Popover
      v-slot="{ open }: { open: boolean }"
      as="div"
    >
      <PopoverButton
        aria-labelledby="account-menu-label"
        class="wc-focus relative flex size-12 items-center justify-center rounded-full bg-accent-200 hover:bg-accent-100 focus-visible:underline-offset-1"
        data-test-id="account-menu-button"
        @click="handlePopoverClick"
        @keydown.enter="handlePopoverClick"
      >
        <span
          id="account-menu-label"
          class="hidden"
        >
          <template v-if="userStore.incompleteRegistrationCount > 0">
            {{ userStore.incompleteRegistrationCount }} incomplete {{ userStore.incompleteRegistrationCount === 1 ? 'registration' : 'registrations' }} —
          </template>
          {{ open ? 'Close Account Menu' : 'Open Account Menu' }}
        </span>
        <span
          class="heading-xs uppercase text-gray-800"
          v-text="`${user?.firstname?.charAt(0)}${user?.lastname?.charAt(0)}`"
        />
        <span
          v-if="userStore.incompleteRegistrationCount > 0"
          aria-hidden="true"
          class="absolute -top-1 right-0.5 flex size-4 items-center justify-center rounded-full border border-white bg-red text-xs font-semibold text-white"
        />
      </PopoverButton>
      <Transition
        enter-active-class="transition duration-300 ease-out motion-reduce:transition-none motion-reduce:hover:transform-none"
        enter-from-class="-translate-y-full md:-translate-y-1 md:opacity-0"
        enter-to-class="translate-y-0 md:opacity-100"
        leave-active-class="transition duration-200 ease-in motion-reduce:transition-none motion-reduce:hover:transform-none"
        leave-from-class="translate-y-0 md:opacity-100"
        leave-to-class="-translate-y-full md:-translate-y-1 md:opacity-0"
      >
        <PopoverPanel
          v-slot="{ close }"
          as="div"
          class="absolute top-0 z-10 w-full rounded-b-2xl bg-white shadow-sm max-md:left-0 md:right-0 md:top-full md:mt-px md:max-w-sm"
        >
          <div class="flex items-center justify-between gap-x-4 bg-gray-100 px-4 py-3">
            <span
              class="body-sm font-semibold text-gray-800"
              v-text="`${user?.firstname} ${user?.lastname}`"
            />
            <button
              class="wc-focus md:hidden"
              type="button"
              @click="close"
            >
              <span
                class="sr-only"
                v-text="'Close Account Menu'"
              />
              <IconX
                :size="24"
                aria-hidden="true"
                class="text-gray-800"
              />
            </button>
          </div>
          <div class="flex flex-col gap-y-4 px-4 pb-4 pt-2">
            <div class="body-sm flex flex-col gap-2 text-gray-800">
              <a
                :href="accountLinks.DASHBOARD"
                class="wc-focus py-2 hover:text-primary-500"
                v-text="'My Dashboard'"
              />
              <a
                :href="accountLinks.PROFILE"
                class="wc-focus py-2 hover:text-primary-500"
                v-text="'Update My Profile'"
              />
              <a
                v-if="userStore.incompleteRegistrationCount > 0"
                :href="accountLinks.DASHBOARD"
                class="wc-focus flex items-center gap-x-2 py-2 hover:text-primary-500"
              >
                <span v-text="`Incomplete Registrations`" />
                <span
                  class="flex size-5 items-center justify-center rounded-full bg-red text-sm font-semibold text-white"
                  v-text="userStore.incompleteRegistrationCount"
                />
              </a>
            </div>
            <button
              class="btn btn--outline"
              type="button"
              @click="logout"
              v-text="'Log Out'"
            />
          </div>
        </PopoverPanel>
      </Transition>
    </Popover>
  </template>

  <a
    v-else
    :href="authLinks.LOGIN"
    class="btn btn--primary max-md:size-12 max-md:rounded-full max-md:p-0"
    data-test-id="login-link"
  >
    <span
      class="max-md:sr-only"
      v-text="'Log In'"
    />
    <IconUserFilled
      :size="24"
      aria-hidden="true"
      class="md:hidden"
    />
  </a>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { IconUserFilled, IconX } from '@tabler/icons-vue'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { useUserStore } from '@js/stores/userStore'

import useAuth from '@js/composables/useAuth'
import { usePopover } from '@js/composables/usePopover'
import { accountLinks, authLinks } from '@js/utils/links'

defineOptions({
  name: 'AppAccountDropdown',
})

const { handlePopoverClick } = usePopover()
const { getAuthUser, isLoggedIn, logout } = useAuth()

const user = computed(() => getAuthUser())

const userStore = useUserStore()

</script>
